import React from 'react';
import SearchableSelect from './SearchableSelect';
import Loader from './Loader';

function AssignmentEditorModal({
  show,
  saving,
  editingAssignment,
  formData,
  employees,
  assets,
  assetFilter,
  setAssetFilter,
  setFormData,
  onClose,
  onSubmit
}) {
  const isDark = document.body.getAttribute('data-theme') === 'dark';
  const compactLabelClass = 'form-label fw-bold text-primary small mb-2';
  const compactSectionCardStyle = { backgroundColor: isDark ? '#2d3748' : '#f8f9fa' };
  const compactInputStyle = {
    backgroundColor: isDark ? '#4a5568' : '#ffffff',
    color: isDark ? '#e2e8f0' : '#495057',
    border: isDark ? '1px solid #4a5568' : '1px solid #ced4da',
    fontSize: '0.95rem',
    minHeight: '40px'
  };
  const getSelectedCount = (assetId) => formData.selectedAssets.filter((asset) => asset.assetId === assetId).length;
  const getAvailableCount = (asset) => asset.quantity - (asset.assignedCount || 0) - (asset.inRepairCount || 0) - (asset.deadCount || 0);
  const getRemainingCount = (asset) => getAvailableCount(asset) - getSelectedCount(asset.id);
  const addSelectedAsset = (assetData) => {
    const asset = assets.find((item) => item.id === assetData.assetId);
    if (!asset || getRemainingCount(asset) <= 0) {
      return;
    }

    setFormData({
      ...formData,
      selectedAssets: [
        ...formData.selectedAssets,
        {
          ...assetData,
          selectionId: `${assetData.assetId}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          serialNumber: ''
        }
      ]
    });
  };
  const filteredAssets = assets.filter((asset) =>
    asset.name.toLowerCase().includes(assetFilter.toLowerCase()) ||
    asset.assetType?.name.toLowerCase().includes(assetFilter.toLowerCase())
  );

  return (
    <div className={`modal fade ${show ? 'show d-block' : ''}`} style={{ backgroundColor: show ? 'rgba(0,0,0,0.5)' : 'transparent' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable" style={{ maxWidth: '1140px', margin: '1rem auto' }}>
        <div className="modal-content border-0 shadow-lg">
          <div className="modal-header bg-primary text-white py-2 px-3">
            <h5 className="modal-title fs-5 mb-0">
              <i className="fas fa-magic me-2"></i>
              {editingAssignment ? 'Edit Assignment Details' : 'Assign Assets to Employee'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          <form onSubmit={onSubmit}>
            <div className="modal-body p-3" style={{ maxHeight: 'calc(100vh - 190px)', overflowY: 'auto' }}>
              <div className="row mb-3">
                <div className="col-12">
                  <div className="card border-0 bg-light">
                    <div className="card-body py-3 px-3">
                      {editingAssignment ? (
                        <>
                          <label className={compactLabelClass}>
                            <i className="fas fa-user me-2"></i>Assigned Employee
                          </label>
                          <div className="form-control form-control-sm bg-light">
                            {editingAssignment.employeeName || editingAssignment.EmployeeName || editingAssignment.employeeId || editingAssignment.EmployeeId || 'Unknown'}
                          </div>
                          <small className="text-muted">Use transfer if you need to move this asset to a different employee.</small>
                        </>
                      ) : (
                        <>
                          <label className={compactLabelClass}>
                            <i className="fas fa-user me-2"></i>Select Employee
                          </label>
                          <SearchableSelect
                            options={employees.map((employee) => ({
                              value: employee.id ?? employee.Id,
                              label: employee.userName || employee.UserName || employee.name || employee.Name || (employee.id ?? employee.Id)
                            }))}
                            value={formData.employeeId}
                            onChange={(e) => {
                              console.log('Employee selected:', e.target.value, 'Type:', typeof e.target.value);
                              setFormData({ ...formData, employeeId: e.target.value });
                            }}
                            placeholder="Choose an employee..."
                            required
                          />
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {!editingAssignment ? (
                <div className="row">
                  <div className="col-md-6">
                    <div className="card h-100 border-primary">
                      <div className="card-header bg-primary text-white d-flex justify-content-between align-items-center py-2 px-3">
                        <h6 className="mb-0 fs-6">
                          <i className="fas fa-boxes me-2"></i>Available Assets
                        </h6>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          placeholder="Filter assets..."
                          value={assetFilter}
                          onChange={(e) => setAssetFilter(e.target.value)}
                          style={{ maxWidth: '180px', fontSize: '0.9rem' }}
                        />
                      </div>
                      <div className="card-body p-2" style={{ height: '300px', overflowY: 'auto' }}>
                        {filteredAssets.map((asset) => {
                          const remainingCount = getRemainingCount(asset);
                          const isSelectable = remainingCount > 0;

                          return (
                            <div
                              key={asset.id}
                              className="card mb-2 shadow-sm border-0"
                              draggable={isSelectable}
                              onDragStart={(e) => {
                                if (!isSelectable) {
                                  e.preventDefault();
                                  return;
                                }

                                e.dataTransfer.setData('text/plain', JSON.stringify({
                                  assetId: asset.id,
                                  assetName: asset.name,
                                  assetType: asset.assetType?.name
                                }));
                              }}
                              onClick={() => addSelectedAsset({
                                assetId: asset.id,
                                assetName: asset.name,
                                assetType: asset.assetType?.name
                              })}
                              style={{
                                cursor: isSelectable ? 'grab' : 'not-allowed',
                                userSelect: 'none',
                                background: isDark
                                  ? 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)'
                                  : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                transition: 'transform 0.2s',
                                border: isDark ? '1px solid #4a5568' : 'none',
                                opacity: isSelectable ? 1 : 0.65
                              }}
                              onMouseEnter={(e) => { if (isSelectable) e.currentTarget.style.transform = 'scale(1.02)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
                            >
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-center">
                                  <div>
                                    <h6 className="mb-1 fw-bold fs-6">{asset.name}</h6>
                                    <small className="opacity-75 small">
                                      <i className="fas fa-tag me-1"></i>{asset.assetType?.name}
                                    </small>
                                  </div>
                                  <div className="text-end">
                                    <span
                                      className="badge fs-6"
                                      style={{
                                        backgroundColor: isDark ? '#e2e8f0' : '#f8f9fa',
                                        color: isDark ? '#1a202c' : '#495057'
                                      }}
                                    >
                                      <i className="fas fa-cube me-1"></i>
                                      {remainingCount}
                                    </span>
                                    <br />
                                    <small className="opacity-75 small">available</small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {filteredAssets.length === 0 && (
                          <div className="text-center text-muted py-4">
                            <i className="fas fa-inbox fa-2x mb-2 opacity-50"></i>
                            <p className="small mb-0">{assetFilter ? 'No assets match your filter' : 'No assets available'}</p>
                          </div>
                        )}
                      </div>
                      <div className="card-footer bg-light text-center py-2">
                        <small className="text-muted small">
                          <i className="fas fa-hand-paper me-1"></i>Drag assets to assign →
                        </small>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-6">
                    <div className="card h-100 border-success">
                      <div className="card-header bg-success text-white py-2 px-3">
                        <h6 className="mb-0 fs-6">
                          <i className="fas fa-check-circle me-2"></i>Selected Assets ({formData.selectedAssets.length})
                        </h6>
                      </div>
                      <div
                        className="card-body p-3"
                        style={{ height: '300px', overflowY: 'auto' }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => {
                          e.preventDefault();
                          const assetData = JSON.parse(e.dataTransfer.getData('text/plain'));
                          addSelectedAsset(assetData);
                        }}
                      >
                        {formData.selectedAssets.length === 0 ? (
                          <div className="text-center text-muted py-4" style={{ border: '2px dashed #28a745', borderRadius: '10px', backgroundColor: '#f8fff9' }}>
                            <i className="fas fa-cloud-download-alt fa-2x mb-2 text-success opacity-50"></i>
                            <h6 className="fs-6">Drop Assets Here</h6>
                            <p className="mb-0 small">Drag assets from the left panel</p>
                          </div>
                        ) : (
                          formData.selectedAssets.map((assetData, index) => (
                            <div key={assetData.selectionId || `${assetData.assetId}-${index}`} className="card mb-2 border-0 shadow-sm" style={{ backgroundColor: '#f8fff9' }}>
                              <div className="card-body p-2">
                                <div className="d-flex justify-content-between align-items-start mb-1">
                                  <div>
                                    <h6 className="mb-1 text-success fw-bold fs-6">{assetData.assetName}</h6>
                                    <small className="text-muted small">
                                      <i className="fas fa-tag me-1"></i>{assetData.assetType}
                                    </small>
                                  </div>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger rounded-circle d-flex align-items-center justify-content-center"
                                    onClick={() => {
                                      setFormData({
                                        ...formData,
                                        selectedAssets: formData.selectedAssets.filter((_, i) => i !== index)
                                      });
                                    }}
                                    title="Remove asset"
                                    style={{ width: '30px', height: '30px' }}
                                  >
                                    <i className="fas fa-times"></i>
                                  </button>
                                </div>
                                <div className="input-group input-group-sm">
                                  <span className="input-group-text bg-success text-white">
                                    <i className="fas fa-barcode"></i>
                                  </span>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    placeholder="Enter serial number"
                                    value={assetData.serialNumber}
                                    onChange={(e) => {
                                      const updated = [...formData.selectedAssets];
                                      updated[index].serialNumber = e.target.value;
                                      setFormData({ ...formData, selectedAssets: updated });
                                    }}
                                    required
                                    style={{ minHeight: '36px', fontSize: '0.9rem' }}
                                  />
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="row">
                  <div className="col-12">
                    <div className="card border-success">
                      <div className="card-header bg-success text-white">
                        <h6 className="mb-0">
                          <i className="fas fa-box me-2"></i>Assigned Asset
                        </h6>
                      </div>
                      <div className="card-body">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <h6 className="mb-1 text-success fw-bold">{formData.selectedAssets[0]?.assetName || 'Unknown Asset'}</h6>
                            <small className="text-muted d-block">
                              <i className="fas fa-tag me-1"></i>{formData.selectedAssets[0]?.assetType || 'N/A'}
                            </small>
                          </div>
                          <span className={`badge ${editingAssignment.isActive ? 'bg-success' : 'bg-secondary'}`}>
                            {editingAssignment.isActive ? 'Active' : 'Returned'}
                          </span>
                        </div>
                        <div className="mt-3">
                          <label className="form-label fw-bold text-primary">
                            <i className="fas fa-barcode me-2"></i>Serial Number
                          </label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter serial number"
                            value={formData.selectedAssets[0]?.serialNumber || ''}
                            onChange={(e) => {
                              const updatedAssets = [...formData.selectedAssets];
                              if (updatedAssets[0]) {
                                updatedAssets[0] = { ...updatedAssets[0], serialNumber: e.target.value };
                                setFormData({ ...formData, selectedAssets: updatedAssets });
                              }
                            }}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card border-0" style={compactSectionCardStyle}>
                    <div className="card-body py-3 px-3">
                      <label className={compactLabelClass}>
                        <i className="fas fa-calendar-alt me-2"></i>Assigned Date
                      </label>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={formData.assignedDate}
                        onChange={(e) => setFormData({ ...formData, assignedDate: e.target.value })}
                        required
                        style={compactInputStyle}
                      />
                    </div>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="card border-0" style={compactSectionCardStyle}>
                    <div className="card-body py-3 px-3">
                      <label className={compactLabelClass}>
                        <i className="fas fa-shipping-fast me-2"></i>Delivery Method
                      </label>
                      <select
                        className="form-select form-select-sm"
                        value={formData.deliveryMethod}
                        onChange={(e) => {
                          const deliveryMethod = parseInt(e.target.value, 10);
                          setFormData({
                            ...formData,
                            deliveryMethod,
                            deliveryStatus: editingAssignment && deliveryMethod === 0 ? 2 : formData.deliveryStatus,
                            courierService: deliveryMethod === 1 ? formData.courierService : '',
                            trackingId: deliveryMethod === 1 ? formData.trackingId : '',
                            pickupPersonName: deliveryMethod === 2 ? formData.pickupPersonName : '',
                            pickupPersonMobile: deliveryMethod === 2 ? formData.pickupPersonMobile : ''
                          });
                        }}
                        required
                        style={compactInputStyle}
                      >
                        <option value={0}>🚶 Self Pickup</option>
                        <option value={1}>🚚 Courier</option>
                        <option value={2}>👥 Other Person</option>
                      </select>
                      {editingAssignment && (
                        <div className="mt-3">
                          <label className={compactLabelClass}>
                            <i className="fas fa-truck-loading me-2"></i>Delivery Status
                          </label>
                          <select
                            className="form-select form-select-sm"
                            value={formData.deliveryStatus}
                            onChange={(e) => setFormData({ ...formData, deliveryStatus: parseInt(e.target.value, 10) })}
                            style={compactInputStyle}
                          >
                            <option value={0}>Pending</option>
                            <option value={1}>In Transit</option>
                            <option value={2}>Delivered</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row mt-3">
                <div className="col-md-6">
                  <div className="card border-0" style={compactSectionCardStyle}>
                    <div className="card-body py-3 px-3">
                      <label className={compactLabelClass}>
                        <i className="fas fa-sticky-note me-2"></i>Notes
                      </label>
                      <textarea
                        className="form-control form-control-sm"
                        rows="2"
                        placeholder="Add any special instructions..."
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        style={{ ...compactInputStyle, minHeight: '72px' }}
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>

              {formData.deliveryMethod === 1 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="card border-warning">
                      <div className="card-header bg-warning text-dark py-2 px-3">
                        <h6 className="mb-0 fs-6">
                          <i className="fas fa-truck me-2"></i>Courier Details
                        </h6>
                      </div>
                      <div className="card-body py-3 px-3">
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label fw-bold small mb-2">
                              <i className="fas fa-building me-2"></i>Courier Service
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g., FedEx, DHL, Blue Dart"
                              value={formData.courierService}
                              onChange={(e) => setFormData({ ...formData, courierService: e.target.value })}
                              required
                              style={compactInputStyle}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small mb-2">
                              <i className="fas fa-tracking me-2"></i>Tracking ID
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Enter tracking number"
                              value={formData.trackingId}
                              onChange={(e) => setFormData({ ...formData, trackingId: e.target.value })}
                              style={compactInputStyle}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {formData.deliveryMethod === 2 && (
                <div className="row mt-3">
                  <div className="col-12">
                    <div className="card border-info">
                      <div className="card-header bg-info text-white py-2 px-3">
                        <h6 className="mb-0 fs-6">
                          <i className="fas fa-user-friends me-2"></i>Pickup Person Details
                        </h6>
                      </div>
                      <div className="card-body py-3 px-3">
                        <div className="row">
                          <div className="col-md-6">
                            <label className="form-label fw-bold small mb-2">
                              <i className="fas fa-user me-2"></i>Person Name
                            </label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="Full name of pickup person"
                              value={formData.pickupPersonName}
                              onChange={(e) => setFormData({ ...formData, pickupPersonName: e.target.value })}
                              required
                              style={compactInputStyle}
                            />
                          </div>
                          <div className="col-md-6">
                            <label className="form-label fw-bold small mb-2">
                              <i className="fas fa-phone me-2"></i>Mobile Number
                            </label>
                            <input
                              type="tel"
                              className="form-control form-control-sm"
                              placeholder="10-digit mobile number"
                              value={formData.pickupPersonMobile}
                              onChange={(e) => setFormData({ ...formData, pickupPersonMobile: e.target.value })}
                              required
                              style={compactInputStyle}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer bg-light border-0 py-2 px-3" style={{ flexShrink: 0 }}>
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                <i className="fas fa-times me-2"></i>Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-primary ${saving ? 'btn-loading' : ''}`}
                disabled={saving || formData.selectedAssets.length === 0 || (!editingAssignment && (!formData.employeeId || formData.employeeId === '')) || (!editingAssignment && formData.selectedAssets.some((asset) => !asset.serialNumber.trim()))}
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
              >
                {saving ? (
                  <>
                    <Loader size="small" />
                    <span className="ms-2">Processing...</span>
                  </>
                ) : (
                  <>
                    <i className="fas fa-magic me-2"></i>
                    {editingAssignment ? 'Update Assignment' : `Assign ${formData.selectedAssets.length} Asset${formData.selectedAssets.length !== 1 ? 's' : ''}`}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AssignmentEditorModal;
